
import React, { useMemo, useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import type { BaseProblem, GeneratedProblem, GenerationJob, NavSelection, ProblemParts, GenerationModel } from '../types';
import ProblemCard from './ProblemCard';
import GenerationJobCard from './GenerationJobCard';
import TrashedProblemCard from './TrashedProblemCard';
import AddBaseProblemModal from './LearnView';
import GenerateCreativeModal from './CreativeGenerateView';
import Spinner from './Spinner';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import toast from 'react-hot-toast';
import { useSelection } from '../contexts/SelectionContext';
import { useDialog } from '../contexts/DialogContext';

// ... (Interfaces and imports remain mostly the same) ...

interface ContentPaneProps {
  selection: NavSelection | null;
  baseProblems: BaseProblem[];
  problemHistory: GeneratedProblem[];
  generationJobs: GenerationJob[];
  trashedProblems: (BaseProblem | GeneratedProblem)[];
  allFolderPaths: string[];
  // Infinite Scroll props
  fetchNextBaseProblems: () => void;
  hasNextBasePage?: boolean;
  isFetchingNextBase: boolean;
  fetchNextGeneratedProblems: () => void;
  hasNextGeneratedPage?: boolean;
  isFetchingNextGenerated: boolean;
  fetchNextTrashed: () => void;
  hasNextTrashedPage?: boolean;
  isFetchingNextTrashed: boolean;
  // Action callbacks
  onRequestGeneration: (jobDetails: any) => Promise<void>;
  onAddMultipleBaseProblems: (problems: Omit<BaseProblem, 'id' | 'duplicateOf' | 'deletedAt'>[]) => Promise<any>;
  onQueueImport: (files: File[], type: 'single' | 'dual', folderName: string) => Promise<void>; 
  onDeleteBaseProblem: (problemId: string) => Promise<void>;
  onDeleteGeneratedProblem: (problemId: string) => Promise<void>;
  onDeleteJob: (jobId: string) => Promise<void>;
  onRestoreProblem: (problem: BaseProblem | GeneratedProblem) => Promise<void>;
  onPermanentlyDeleteProblem: (problem: BaseProblem | GeneratedProblem) => Promise<void>;
  onDeleteMultipleGeneratedProblems: (problemIds: string[]) => Promise<void>;
  onDeleteMultipleBaseProblems: (problemIds: string[]) => Promise<void>;
  onPermanentlyDeleteAllTrashed: () => Promise<void>;
  onRegenerateExplanation: (problemId: string, model?: GenerationModel) => Promise<void>;
  onCancelExplanationGeneration: (problemId: string) => Promise<void>;
  onOpenPrintPreview: (problems: (BaseProblem | GeneratedProblem)[]) => void;
  onUpdateProblemContent: (problemId: string, isGenerated: boolean, newParts: ProblemParts, newExplanation: string | null) => Promise<void>;
  onAddFolder: (path: string) => Promise<void>;
  onRenameFolder: (oldPath: string, newPath: string) => Promise<void>;
  onDeleteFolder: (folderPath: string) => Promise<void>;
  onAiSearch: (query: string, problems: (BaseProblem | GeneratedProblem)[]) => Promise<string[]>;
  onSelect: (selection: NavSelection) => void;
  problemToMove: { id: string; isGenerated: boolean } | null;
  setProblemToMove: (problem: { id: string; isGenerated: boolean } | null) => void;
  onMoveProblem: (problemId: string, isGenerated: boolean, newFolderPath: string) => Promise<void>;
  onCancelJob: (jobId: string) => Promise<void>;
  jobToRetry: GenerationJob | null;
  setJobToRetry: (job: GenerationJob | null) => void;
  setProblemToEdit: (problem: { problem: BaseProblem | GeneratedProblem, isGenerated: boolean } | null) => void;
  onOpenGenerateModifiedModal: () => void;
  isSelectionMoving: boolean;
  setIsSelectionMoving: (isMoving: boolean) => void;
  onFixProblemData?: (problemId: string, isGenerated: boolean, target: 'problem' | 'explanation') => Promise<void>;
  onToggleLock: (problemId: string, isGenerated: boolean, newLockedState: boolean) => Promise<void>;
}

// ... (SortableProblemCard, FolderIconDisplay, DraggableDroppableFolder, NewFolderInput, FolderStackLevel, FolderViewContent, components kept as is) ...
const SortableProblemCard = React.forwardRef(({ item, onContextMenu, onRegenerateExplanation, onCancelExplanationGeneration, onFixProblemData, onToggleLock, shouldShake, disabled, ...props }: { item: BaseProblem | GeneratedProblem, onContextMenu: (e: React.MouseEvent) => void, onRegenerateExplanation: (id: string) => void, onCancelExplanationGeneration: (id: string) => void, onFixProblemData?: (id: string, isGen: boolean, target: 'problem' | 'explanation') => Promise<void>, onToggleLock: (id: string, isGen: boolean, locked: boolean) => Promise<void>, shouldShake: boolean, disabled?: boolean } & any, ref: any) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ 
        id: item.id,
        data: { problemId: item.id, isGenerated: 'generatedParts' in item, type: 'problem', orderIndex: item.orderIndex },
        disabled: disabled 
    });
    
    const { isSelected, toggleSelection } = useSelection();

    const style: React.CSSProperties = { 
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
        zIndex: isDragging ? 999 : 'auto',
        height: '100%', 
    };
    
    const setRefs = (node: HTMLElement | null) => {
        setNodeRef(node);
        if (ref) {
            if (typeof ref === 'function') {
                ref(node);
            } else {
                ref.current = node;
            }
        }
    };

    return (
        <div ref={setRefs} style={style} {...attributes} {...listeners} onContextMenu={onContextMenu} className={`relative group select-none h-full transition-opacity duration-300 ease-out`}>
            <ProblemCard 
              problem={item} 
              selectionMode={true}
              isSelected={isSelected(item.id)}
              onSelectToggle={toggleSelection} 
              onRegenerateExplanation={onRegenerateExplanation}
              onCancelExplanationGeneration={onCancelExplanationGeneration}
              onFixProblemData={onFixProblemData}
              onToggleLock={onToggleLock}
              shouldShake={shouldShake}
              {...props} 
            />
        </div>
    );
});

// ... (Folder Components: FolderIconDisplay, DraggableDroppableFolder, NewFolderInput, FolderStackLevel, FolderViewContent - keep unchanged) ...
const FolderIconDisplay: React.FC = () => (
    <div className="relative w-12 h-10 min-w-[3rem] min-h-[2.5rem] flex-shrink-0 drop-shadow-md transition-transform group-hover:scale-105">
        <svg viewBox="0 0 24 24" fill="none" className="absolute top-0 left-0 w-full h-full">
             <path d="M3 11V6C3 4.89543 3.89543 4 5 4H11L13 6H19C20.1046 6 21 6.89543 21 8V11H3Z" fill="#52525b" />
             <path d="M21 11H3V19.5C3 20.3284 3.67157 21 4.5 21H19.5C20.3284 21 21 20.3284 21 19.5V11Z" fill="#71717a" />
             <path d="M21.5 11L19.5 21H4.5L2.5 11H21.5Z" fill="#a1a1aa" fillOpacity="0.9" />
        </svg>
    </div>
);

const DraggableDroppableFolder: React.FC<{ folder: any; onClick: (id: string) => void; onContextMenu: (e: React.MouseEvent) => void; isActive?: boolean; disabled?: boolean }> = ({ folder, onClick, onContextMenu, isActive, disabled }) => {
    const droppableId = `folder-${folder.id}`;
    const { attributes, listeners, setNodeRef: setDraggableRef, isDragging } = useDraggable({ 
        id: droppableId, 
        data: { folderPath: folder.id, type: 'folder' },
        disabled: disabled
    });
    const { isOver, setNodeRef: setDroppableRef } = useDroppable({ 
        id: droppableId, 
        data: { folderPath: folder.id, type: 'folder' },
        disabled: disabled 
    });
    const combinedRef = (node: HTMLElement | null) => { setDraggableRef(node); setDroppableRef(node); };
    const style: React.CSSProperties = { opacity: isDragging ? 0.3 : 1 };

    return (
        <div ref={combinedRef} style={style} {...attributes} {...listeners} onClick={() => onClick(folder.id)} onContextMenu={onContextMenu} 
            className={`flex flex-col items-center justify-center p-2 bg-[var(--color-bg-glass)] border rounded-lg cursor-pointer hover:bg-white/10 hover:border-[var(--color-text-secondary)]/50 transition-all duration-200 select-none touch-none group ${isOver ? 'ring-2 ring-zinc-500 bg-zinc-500/20' : 'border-[var(--color-border-glass)]'} ${isActive ? 'ring-2 ring-[var(--color-accent)] bg-[var(--color-accent)]/10' : ''}`} 
            data-selectable-item="true"
        >
            <div className="mb-1 flex-shrink-0"><FolderIconDisplay /></div>
            <span className={`text-xs font-semibold text-center truncate w-full px-1 leading-tight ${isActive ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-primary)]'}`}>{folder.name}</span>
            <span className="text-[10px] text-[var(--color-text-tertiary)]">{folder.count} 항목</span>
        </div>
    );
};

const NewFolderInput: React.FC<{ parentPath: string; onCreate: (parentPath: string, name: string) => void; onCancel: () => void; }> = ({ parentPath, onCreate, onCancel }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => { inputRef.current?.focus(); }, []);
    return (
        <div className="flex flex-col items-center justify-center p-2 bg-[var(--color-bg-glass)] border-2 border-dashed border-[var(--color-accent)] rounded-lg">
            <div className="mb-1 flex-shrink-0 w-12 h-10 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-zinc-500/50"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
            </div>
            <input ref={inputRef} type="text" placeholder="폴더명" className="text-xs font-semibold text-center bg-transparent w-full focus:outline-none border-b border-white/20 pb-0.5 text-[var(--color-text-primary)]" onKeyDown={e => { if (e.key === 'Enter') onCreate(parentPath, e.currentTarget.value); if (e.key === 'Escape') onCancel(); }} onBlur={e => onCreate(parentPath, e.currentTarget.value)} />
        </div>
    );
};

const FolderStackLevel: React.FC<{
    path: string;
    index: number;
    data: { folders: {id: string, name: string, count: number}[], problems: any[] };
    nextActivePath?: string;
    activePathsLength: number;
    creatingFolderInPath: string | null;
    onBreadcrumbClick: (path: string) => void;
    onFolderClick: (path: string, levelIndex: number) => void;
    setCreatingFolderInPath: (path: string | null) => void;
    onCreateFolder: (parentPath: string, name: string) => void;
    setContextMenu: (menu: any) => void;
    disabled?: boolean;
}> = ({ 
    path, index, data, nextActivePath, activePathsLength, creatingFolderInPath, 
    onBreadcrumbClick, onFolderClick, setCreatingFolderInPath, onCreateFolder, setContextMenu, disabled 
}) => {
    const subfolders = data.folders.sort((a,b) => a.name.localeCompare(b.name));
    const breadcrumbPath = path === '/' ? '문제 라이브러리' : path.split('/').pop();
    
    const { isOver, setNodeRef } = useDroppable({
        id: `folder-stack-${path}`,
        data: { folderPath: path, type: 'folder' },
        disabled: disabled
    });

    if (subfolders.length === 0 && creatingFolderInPath !== path && index < activePathsLength - 1) return null;

    return (
          <div className="border-b border-[var(--color-border-glass)] bg-[var(--color-bg-secondary)]/30">
              <div 
                ref={setNodeRef}
                className={`px-6 py-3 flex items-center gap-2 border-b border-[var(--color-border-glass)] transition-colors ${isOver ? 'bg-[var(--color-accent)]/20' : 'bg-[var(--color-bg-secondary)]'}`}
              >
                  <span 
                    onClick={() => onBreadcrumbClick(path)}
                    className="text-zinc-500 font-normal text-sm cursor-pointer hover:text-[var(--color-accent)] p-1 rounded hover:bg-white/5 transition-colors select-none"
                    title="이 폴더로 이동"
                  >
                    {index === 0 ? '📂' : '↳'}
                  </span>
                  <button onClick={() => onBreadcrumbClick(path)} className="font-bold text-lg hover:underline text-[var(--color-text-primary)]">{breadcrumbPath}</button>
                  <span className="text-xs text-zinc-500 ml-2 bg-zinc-800 px-2 py-0.5 rounded-full">{subfolders.length} 폴더 / {data.problems.length} 문제</span>
                  <div className="flex-grow"></div>
                  <button onClick={() => setCreatingFolderInPath(path)} className="p-2 rounded-full hover:bg-white/10 text-zinc-400 hover:text-white" title="새 하위 폴더">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>
                  </button>
              </div>
              {(subfolders.length > 0 || creatingFolderInPath === path) ? (
                 <div className="p-4 grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3">
                     {subfolders.map(folder => (
                         <DraggableDroppableFolder key={folder.id} folder={folder} onClick={() => onFolderClick(folder.id, index)} onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setContextMenu({ x: e.clientX, y: e.clientY, item: { id: folder.id, name: folder.name }, type: 'folder' }); }} isActive={nextActivePath === folder.id} disabled={disabled} />
                     ))}
                     {creatingFolderInPath === path && ( <NewFolderInput parentPath={path} onCreate={onCreateFolder} onCancel={() => setCreatingFolderInPath(null)} /> )}
                 </div>
              ) : ( index === activePathsLength - 1 && <div className="p-4 text-center text-sm text-zinc-500">하위 폴더가 없습니다.</div> )}
          </div>
    );
};

const FolderViewContent: React.FC<{
    path: string;
    isActive: boolean;
    fileSystemIndex: Map<string, { folders: {id: string, name: string, count: number}[], problems: (BaseProblem | GeneratedProblem)[] }>;
    creatingFolderInPath: string | null;
    onBreadcrumbClick: (path: string) => void;
    onFolderClick: (path: string, levelIndex: number) => void;
    setCreatingFolderInPath: (path: string | null) => void;
    onCreateFolder: (parentPath: string, name: string) => void;
    setContextMenu: (menu: any) => void;
    filteredIds: Set<string> | null;
    isAiSearching: boolean;
    hasNextBasePage?: boolean;
    isFetchingNextBase: boolean;
    hasNextGeneratedPage?: boolean;
    isFetchingNextGenerated: boolean;
    fetchNextBaseProblems: () => void;
    fetchNextGeneratedProblems: () => void;
    loadMoreRef: (node: HTMLDivElement | null) => void;
    itemRefs: React.MutableRefObject<Map<string, HTMLElement>>;
    onRegenerateExplanation: (id: string) => void;
    onCancelExplanationGeneration: (id: string) => void;
    onFixProblemData?: (id: string, isGen: boolean, target: 'problem' | 'explanation') => Promise<void>;
    onToggleLock: (id: string, isGen: boolean, locked: boolean) => Promise<void>;
    shakingProblemId: string | null;
    parentRef: React.RefObject<HTMLDivElement>;
}> = ({
    path, isActive, fileSystemIndex, creatingFolderInPath, onBreadcrumbClick, onFolderClick, setCreatingFolderInPath, onCreateFolder, setContextMenu,
    filteredIds, isAiSearching, hasNextBasePage, isFetchingNextBase, hasNextGeneratedPage, isFetchingNextGenerated, fetchNextBaseProblems, fetchNextGeneratedProblems, loadMoreRef, itemRefs,
    onRegenerateExplanation, onCancelExplanationGeneration, onFixProblemData, onToggleLock, shakingProblemId, parentRef
}) => {
    const activePaths = useMemo(() => {
        if (path === '/') return ['/'];
        const parts = path.split('/').filter(p => p);
        const paths = ['/'];
        let current = '';
        parts.forEach((part) => { current = current ? `${current}/${part}` : part; paths.push(current); });
        return paths;
    }, [path]);

    const data = fileSystemIndex.get(path) || { folders: [], problems: [] };
    const problems = filteredIds ? data.problems.filter(p => filteredIds.has(p.id)) : data.problems;
    const sortedProblems = problems.sort((a, b) => {
        if (a.orderIndex !== undefined && b.orderIndex !== undefined) return b.orderIndex - a.orderIndex;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return (
        <>
            {activePaths.map((stackPath, index) => (
                <FolderStackLevel 
                    key={stackPath} 
                    path={stackPath} 
                    index={index} 
                    data={fileSystemIndex.get(stackPath) || { folders: [], problems: [] }}
                    nextActivePath={activePaths[index + 1]}
                    activePathsLength={activePaths.length}
                    creatingFolderInPath={creatingFolderInPath}
                    onBreadcrumbClick={onBreadcrumbClick}
                    onFolderClick={onFolderClick}
                    setCreatingFolderInPath={setCreatingFolderInPath}
                    onCreateFolder={onCreateFolder}
                    setContextMenu={setContextMenu}
                    disabled={!isActive}
                />
            ))}
            
            {isAiSearching ? (
                <div className="p-8 flex justify-center"><Spinner message="AI가 문제를 검색하고 있습니다..." /></div>
            ) : (problems.length === 0 && !filteredIds) ? (
                <div className="p-8 text-center text-sm text-zinc-500"><p>현재 폴더에 문제가 없습니다.</p><p className="mt-2">오른쪽 상단의 '새 기반문제 추가' 버튼으로 문제를 추가하세요.</p></div>
            ) : (
                <SortableContext items={sortedProblems.map(p => p.id)} strategy={rectSortingStrategy} disabled={!isActive}>
                    <div className="p-4 grid grid-cols-1 md:grid-cols-[repeat(auto-fill,minmax(500px,1fr))] gap-4" style={{ minHeight: '200px' }}>
                        {sortedProblems.map(p => (
                            <SortableProblemCard 
                                key={p.id} 
                                ref={(el: HTMLDivElement) => { if (el && isActive) itemRefs.current.set(p.id, el); else if (!isActive) itemRefs.current.delete(p.id); }} 
                                item={p} 
                                onContextMenu={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); setContextMenu({x: e.clientX, y: e.clientY, item: p, type: 'problem' });}} 
                                onRegenerateExplanation={onRegenerateExplanation} 
                                onCancelExplanationGeneration={onCancelExplanationGeneration} 
                                onFixProblemData={onFixProblemData}
                                onToggleLock={onToggleLock}
                                shouldShake={shakingProblemId === p.id}
                                disabled={!isActive}
                            />
                        ))}
                    </div>
                    {(hasNextBasePage || hasNextGeneratedPage) && (
                        <div ref={isActive ? loadMoreRef : null} className="col-span-full p-4 flex justify-center">{(isFetchingNextBase || isFetchingNextGenerated) ? <Spinner message="더 많은 문제 로딩 중..." /> : <span className="text-sm text-zinc-600">더 많은 문제를 보려면 스크롤하세요...</span>}</div>
                    )}
                </SortableContext>
            )}
        </>
    );
};

const ContentPane: React.FC<ContentPaneProps> = (props) => {
  const { selection, baseProblems, problemHistory, generationJobs, trashedProblems, allFolderPaths, onOpenPrintPreview, onAiSearch, onSelect, onAddFolder, onRenameFolder, onDeleteFolder, problemToMove, setProblemToMove, onMoveProblem, setJobToRetry, jobToRetry, setProblemToEdit, onOpenGenerateModifiedModal, fetchNextBaseProblems, hasNextBasePage, isFetchingNextBase, fetchNextGeneratedProblems, hasNextGeneratedPage, isFetchingNextGenerated, fetchNextTrashed, hasNextTrashedPage, isFetchingNextTrashed, onQueueImport, isSelectionMoving, setIsSelectionMoving, onFixProblemData, onToggleLock, ...actions } = props;
  
  const { selectedIds, clearSelection: clearSelectedItems, setSelection, toggleSelection, isSelected } = useSelection();
  const dialog = useDialog();
  
  const [isAddBaseModalOpen, setAddBaseModalOpen] = useState(false);
  const [isGenerateCreativeModalOpen, setGenerateCreativeModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAiSearching, setIsAiSearching] = useState(false);
  const [filteredIds, setFilteredIds] = useState<Set<string> | null>(null);
  const [creatingFolderInPath, setCreatingFolderInPath] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{x: number, y: number, item: any, type: 'folder' | 'problem' } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [selectionBox, setSelectionBox] = useState<{ startX: number, startY: number, currentX: number, currentY: number, isActive: boolean } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const lastMousePos = useRef<{ x: number, y: number } | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [shakingProblemId, setShakingProblemId] = useState<string | null>(null);

  const [visitedPaths, setVisitedPaths] = useState<Set<string>>(new Set(['/']));
  const scrollPositions = useRef<Map<string, number>>(new Map());
  const currentPathRef = useRef<string | null>(null);

  // ... (IntersectionObserver and other hooks logic remains same) ...
  const loadMoreRef = useCallback((node: HTMLDivElement | null) => {
    if (isFetchingNextBase || isFetchingNextGenerated || isFetchingNextTrashed) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
            if (selection?.type === 'folder' && (hasNextBasePage || hasNextGeneratedPage)) {
                if (hasNextBasePage) fetchNextBaseProblems();
                if (hasNextGeneratedPage) fetchNextGeneratedProblems();
            } else if (selection?.type === 'trash' && hasNextTrashedPage) fetchNextTrashed();
        }
    });
    if (node && observerRef.current) observerRef.current.observe(node);
  }, [isFetchingNextBase, isFetchingNextGenerated, isFetchingNextTrashed, hasNextBasePage, hasNextGeneratedPage, hasNextTrashedPage, selection, fetchNextBaseProblems, fetchNextGeneratedProblems, fetchNextTrashed]);

  useEffect(() => {
    if (selection?.type === 'folder') {
        const path = selection.id;
        if (currentPathRef.current && containerRef.current) {
            scrollPositions.current.set(currentPathRef.current, containerRef.current.scrollTop);
        }
        setVisitedPaths(prev => {
            const next = new Set(prev);
            next.add(path);
            return next;
        });
        currentPathRef.current = path;
        clearSelectedItems();
        requestAnimationFrame(() => {
            if (containerRef.current) {
                const savedScroll = scrollPositions.current.get(path) || 0;
                containerRef.current.scrollTop = savedScroll;
            }
        });
    } else {
        currentPathRef.current = null; 
        if (containerRef.current) containerRef.current.scrollTop = 0;
    }
    setSearchQuery(''); setFilteredIds(null); setIsAiSearching(false); setCreatingFolderInPath(null); setContextMenu(null);
  }, [selection, clearSelectedItems]);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(event.target as Node)) setContextMenu(null); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fileSystemIndex = useMemo(() => {
    const index = new Map<string, { folders: {id: string, name: string, count: number}[], problems: (BaseProblem | GeneratedProblem)[] }>();
    index.set('/', { folders: [], problems: [] });
    allFolderPaths.forEach(path => {
        const parts = path.split('/');
        let current = '';
        for (let i = 0; i < parts.length; i++) {
          current = i === 0 ? parts[i] : `${current}/${parts[i]}`;
          if (!index.has(current)) index.set(current, { folders: [], problems: [] });
        }
    });
    Array.from(index.keys()).sort().forEach(path => {
      if (path === '/') return;
      const parts = path.split('/');
      const name = parts.pop()!;
      const parentPath = parts.join('/') || '/';
      const parentData = index.get(parentPath);
      if (parentData && !parentData.folders.some(f => f.id === path)) parentData.folders.push({ id: path, name, count: 0 });
    });
    [...baseProblems, ...problemHistory].forEach(p => {
        const folder = p.folder || '/';
        if (!index.has(folder)) index.set(folder, { folders: [], problems: [] });
        index.get(folder)!.problems.push(p);
    });
    const calculateCounts = (path: string): number => {
        const data = index.get(path);
        if (!data) return 0;
        let count = data.problems.length;
        data.folders.forEach(f => { const childCount = calculateCounts(f.id); f.count = childCount; count += childCount; });
        return count;
    };
    calculateCounts('/');
    return index;
  }, [baseProblems, problemHistory, allFolderPaths]);

  const { title, type } = useMemo(() => {
    if (!selection) return { title: "출.정", type: 'home' };
    switch (selection.type) {
      case 'suneung-home': return { title: "Home", type: 'home' };
      case 'folder': return { title: '문제 라이브러리', type: 'folder' };
      case 'jobs': return { title: "작업 관리자", type: 'job' };
      case 'trash': return { title: "휴지통", type: 'trash' };
      default: return { title: "선택 없음", type: 'none' };
    }
  }, [selection]);
  
  // ... (Handlers mostly same) ...
  const handleFolderClick = (path: string, levelIndex: number) => {
      if (problemToMove) {
          onMoveProblem(problemToMove.id, problemToMove.isGenerated, path);
          setProblemToMove(null);
          toast.success('문제가 이동되었습니다.');
          return;
      }
      onSelect({ type: 'folder', id: path });
  };

  const handleCreateFolder = async (parentPath: string, folderName: string) => {
    if (!folderName.trim()) { setCreatingFolderInPath(null); return; }
    const newPath = parentPath === '/' ? folderName.trim() : `${parentPath}/${folderName.trim()}`;
    onAddFolder(newPath);
    setCreatingFolderInPath(null);
  };
  
  const handleBreadcrumbClick = (path: string) => {
      onSelect({ type: 'folder', id: path });
  };
  
  const handleRenameFolderAction = async (id: string, currentName: string) => {
      const newName = await dialog.prompt('새 폴더 이름:', { defaultValue: currentName });
      if(newName) {
          const parent = id.includes('/') ? id.substring(0, id.lastIndexOf('/')) : '';
          const newPath = parent ? `${parent}/${newName}` : newName;
          onRenameFolder(id, newPath); 
      }
  };

  const handleDeleteFolderAction = async (id: string) => {
      const confirmed = await dialog.confirm('정말로 이 폴더를 삭제하시겠습니까? 내부의 모든 문제도 휴지통으로 이동됩니다.', { destructive: true });
      if(confirmed) onDeleteFolder(id); 
  };

  const handleMoveToTrashAction = async (id: string, isGenerated: boolean) => {
      if (isGenerated) await actions.onDeleteGeneratedProblem(id);
      else await actions.onDeleteBaseProblem(id);
  };
  
  const handleEmptyTrashAction = async () => {
      const confirmed = await dialog.confirm(`${trashedProblems.length}개의 항목을 모두 영구 삭제하시겠습니까?`, { destructive: true });
      if(confirmed) actions.onPermanentlyDeleteAllTrashed();
  };
  
  const handleDeleteSelectionAction = async () => {
      const confirmed = await dialog.confirm(`선택한 ${selectedIds.size}개의 문제를 삭제하시겠습니까?`, { destructive: true });
      if (confirmed) {
          const itemsToDelete: { id: string, isGenerated: boolean }[] = [];
          selectedIds.forEach(id => {
             const problem = [...baseProblems, ...problemHistory].find(p => p.id === id);
             if (problem) itemsToDelete.push({ id: problem.id, isGenerated: 'generatedParts' in problem });
          });
          const baseIds = itemsToDelete.filter(i => !i.isGenerated).map(i => i.id);
          const genIds = itemsToDelete.filter(i => i.isGenerated).map(i => i.id);
          
          if (baseIds.length > 0) await actions.onDeleteMultipleBaseProblems(baseIds);
          if (genIds.length > 0) await actions.onDeleteMultipleGeneratedProblems(genIds);
          
          toast.success('선택된 항목이 삭제되었습니다.');
          clearSelectedItems();
      }
  };
  
  const handleMoveSelectionAction = () => {
      setIsSelectionMoving(true);
      toast('이동할 폴더를 좌측 메뉴에서 선택하세요.', { icon: '📂', duration: 4000 });
  };

  const handleBulkFixMath = async (target: 'problem' | 'explanation') => {
      if (!onFixProblemData) return;
      const toastId = toast.loading(`${selectedIds.size}개 항목의 ${target === 'problem' ? '문제' : '해설'} 수식을 교정합니다...`);
      
      // Since fix is now a job, we just queue them up
      const promises = Array.from(selectedIds).map(async (id) => {
          const problem = [...baseProblems, ...problemHistory].find(p => p.id === id);
          if (!problem) return;
          const isGenerated = 'generatedParts' in problem;
          await onFixProblemData(id, isGenerated, target);
      });

      try {
          await Promise.all(promises);
          toast.success('수식 교정 작업이 요청되었습니다. 작업 관리자를 확인하세요.', { id: toastId });
      } catch (e) {
          toast.error('일부 작업 요청에 실패했습니다.', { id: toastId });
      }
  };

  const handleBulkRegenerateExplanation = async (model: GenerationModel) => {
      const isPro = model === 'gemini-2.5-pro';
      const confirmed = await dialog.confirm(`선택한 ${selectedIds.size}개 문제의 해설을 ${isPro ? '다정(Pro)' : '빠른(Flash)'} 모델로 다시 작성하시겠습니까?\n\n기존 해설은 덮어씌워집니다.`, { destructive: false });
      if (!confirmed) return;

      const toastId = toast.loading(`${selectedIds.size}개 항목의 해설 생성을 요청했습니다...`);
      
      const promises = Array.from(selectedIds).map(async (id) => {
          await actions.onRegenerateExplanation(id, model);
      });

      try {
          await Promise.all(promises);
          toast.success('해설 생성 요청이 완료되었습니다. 작업 관리자를 확인하세요.', { id: toastId });
      } catch (e) {
          toast.error('요청 중 오류가 발생했습니다.', { id: toastId });
      }
  };

  // ... (Other handlers) ...
  const handleCreateWorksheetAction = () => {
      const selectedProblems: (BaseProblem | GeneratedProblem)[] = [];
      selectedIds.forEach(id => {
          const problem = [...baseProblems, ...problemHistory].find(p => p.id === id);
          if (problem) selectedProblems.push(problem);
      });
      if (selectedProblems.length === 0) { toast.error('학습지로 만들 문제를 먼저 선택해주세요.'); return; }
      selectedProblems.sort((a, b) => {
          if (a.orderIndex !== undefined && b.orderIndex !== undefined) return b.orderIndex - a.orderIndex;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      onOpenPrintPreview(selectedProblems);
  };

  const handleCreateModifiedProblemAction = () => {
      if (selectedIds.size === 0) { toast.error('변형할 문제를 먼저 선택해주세요.'); return; }
      onOpenGenerateModifiedModal();
  };

  const handleContentEditAction = (item: any) => {
      if (item.isLocked !== false) {
          setShakingProblemId(item.id);
          setTimeout(() => setShakingProblemId(null), 500);
          return;
      }
      setProblemToEdit({problem: item, isGenerated: 'generatedParts' in item}); 
      setContextMenu(null);
  };

  // ... (Keyboard, Mouse logic) ...
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete') {
        const activeElement = document.activeElement;
        const isInputActive = activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || (activeElement as HTMLElement).isContentEditable);
        if (!isInputActive && selectedIds.size > 0) handleDeleteSelectionAction();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIds, handleDeleteSelectionAction]);

  const handleMouseDown = (e: React.MouseEvent) => {
    const container = e.currentTarget as HTMLElement;
    const target = e.target as HTMLElement;
    if (target.closest('[data-selectable-item]') || e.clientX >= container.clientWidth || e.button !== 0) return;
    const rect = container.getBoundingClientRect();
    const startX = e.clientX - rect.left + container.scrollLeft;
    const startY = e.clientY - rect.top + container.scrollTop;
    lastMousePos.current = { x: e.clientX, y: e.clientY };
    setSelectionBox({ startX, startY, currentX: startX, currentY: startY, isActive: true });
    if (!e.shiftKey && !e.ctrlKey && !e.metaKey) clearSelectedItems();
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
      if (!containerRef.current) return;
      lastMousePos.current = { x: e.clientX, y: e.clientY };
      const container = containerRef.current;
      const currentX = e.clientX - container.getBoundingClientRect().left + container.scrollLeft;
      const currentY = e.clientY - container.getBoundingClientRect().top + container.scrollTop;
      setSelectionBox(prev => { if (!prev?.isActive) return prev; return { ...prev, currentX, currentY }; });
  }, []);

  const handleScroll = useCallback(() => {
      if (!containerRef.current || !lastMousePos.current) return;
      if (currentPathRef.current) {
          scrollPositions.current.set(currentPathRef.current, containerRef.current.scrollTop);
      }
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const { x, y } = lastMousePos.current;
      const currentX = x - rect.left + container.scrollLeft;
      const currentY = y - rect.top + container.scrollTop;
      setSelectionBox(prev => { if (!prev?.isActive) return prev; return { ...prev, currentX, currentY }; });
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!selectionBox?.isActive || !containerRef.current) { if (selectionBox) setSelectionBox(null); return; }
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const boxLeft_viewport = Math.min(selectionBox.startX, selectionBox.currentX) - container.scrollLeft + rect.left;
    const boxTop_viewport = Math.min(selectionBox.startY, selectionBox.currentY) - container.scrollTop + rect.top;
    const boxWidth = Math.abs(selectionBox.currentX - selectionBox.startX);
    const boxHeight = Math.abs(selectionBox.currentY - selectionBox.startY);
    const boxRight_viewport = boxLeft_viewport + boxWidth;
    const boxBottom_viewport = boxTop_viewport + boxHeight;

    if (boxWidth > 5 || boxHeight > 5) {
        const newSelectedIds = new Set(selectedIds);
        itemRefs.current.forEach((element, id) => {
            if (element.offsetParent !== null) {
                const itemRect = element.getBoundingClientRect();
                if (boxLeft_viewport < itemRect.right && boxRight_viewport > itemRect.left && boxTop_viewport < itemRect.bottom && boxBottom_viewport > itemRect.top) newSelectedIds.add(id);
            }
        });
        setSelection(newSelectedIds);
    }
    setSelectionBox(null);
  }, [selectionBox, selectedIds, setSelection]);

  useEffect(() => {
      if (selectionBox?.isActive) { window.addEventListener('mousemove', handleMouseMove); window.addEventListener('mouseup', handleMouseUp); }
      return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [selectionBox?.isActive, handleMouseMove, handleMouseUp]);

  // --- Render Helpers ---
  const renderHome = () => ( <div className="p-8"><h2 className="text-3xl font-bold text-[var(--color-text-primary)]">환영합니다!</h2><p className="mt-2 text-[var(--color-text-secondary)]">좌측 메뉴를 통해 문제 라이브러리를 탐색하거나 새로운 작업을 시작하세요.</p></div> );
  
  const renderJobs = () => {
      const activeJobs = generationJobs.filter(j => j.status !== 'completed' && j.status !== 'failed');
      
      const importJobs = activeJobs.filter(j => j.jobType === 'import');
      const variationJobs = activeJobs.filter(j => j.jobType === 'generation' && j.modificationLevel !== 4);
      const creationJobs = activeJobs.filter(j => j.jobType === 'generation' && j.modificationLevel === 4);
      const expJobs = activeJobs.filter(j => j.jobType === 'explanation');
      const fixJobs = activeJobs.filter(j => j.jobType === 'fix');

      const renderJobSection = (title: string, jobs: GenerationJob[], emptyMsg: string, accentColorFrom: string, accentColorTo: string) => (
          <div className="flex-1 min-w-[280px] h-full flex flex-col bg-[var(--color-bg-secondary)] border-r border-[var(--color-border)] last:border-r-0">
              <div className="p-3 border-b border-[var(--color-border)] flex justify-between items-center sticky top-0 bg-[var(--color-bg-secondary)] z-10 backdrop-blur-sm bg-opacity-90">
                  <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full bg-gradient-to-br ${accentColorFrom} ${accentColorTo} shadow-[0_2px_4px_rgba(0,0,0,0.3)] ring-1 ring-white/10`}></div>
                      <h3 className="font-bold text-xs text-[var(--color-text-secondary)] uppercase tracking-wider">{title}</h3>
                  </div>
                  <span className="text-[10px] font-bold text-[var(--color-text-tertiary)] bg-[var(--color-bg-primary)] px-2 py-0.5 rounded-full border border-[var(--color-border)]">
                      {jobs.length}
                  </span>
              </div>
              <div className="flex-grow overflow-y-auto p-3 space-y-3 custom-scrollbar">
                  {jobs.length === 0 ? (
                      <div className="h-24 flex items-center justify-center text-xs text-[var(--color-text-tertiary)] italic border-2 border-dashed border-[var(--color-border)] rounded-lg m-1 opacity-50">
                          {emptyMsg}
                      </div>
                  ) : (
                      jobs.map(job => (
                          <GenerationJobCard 
                              key={job.id} 
                              job={job} 
                              onDeleteJob={actions.onDeleteJob} 
                              onCancelJob={actions.onCancelJob} 
                              onRetryJob={(j) => { setJobToRetry(j); if (j.modificationLevel === 4) setGenerateCreativeModalOpen(true); else onOpenGenerateModifiedModal(); }} 
                          />
                      ))
                  )}
              </div>
          </div>
      );

      return (
          <div className="h-full flex flex-col bg-[var(--color-bg-primary)]">
              <div className="flex-shrink-0 px-6 py-4 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)] flex justify-between items-end">
                  <div>
                      <h2 className="text-xl font-bold text-[var(--color-text-primary)]">작업 보드</h2>
                      <p className="text-xs text-[var(--color-text-secondary)] mt-1">실시간 진행 상황을 모니터링합니다.</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[var(--color-text-secondary)]">
                      <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 shadow-sm"></div>
                          <span>인식</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 shadow-sm"></div>
                          <span>변형</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 shadow-sm"></div>
                          <span>생성</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 shadow-sm"></div>
                          <span>해설</span>
                      </div>
                  </div>
              </div>
              
              <div className="flex-grow flex overflow-x-auto overflow-y-hidden">
                  {renderJobSection("인식 (Recognition)", importJobs, "대기 중인 파일 없음", "from-indigo-400", "to-indigo-600")}
                  {renderJobSection("변형 (Variation)", variationJobs, "변형 작업 없음", "from-sky-400", "to-sky-600")}
                  {renderJobSection("생성 (Creation)", creationJobs, "창작 작업 없음", "from-teal-400", "to-teal-600")}
                  {renderJobSection("해설 (Explanation)", expJobs, "해설 작업 없음", "from-purple-400", "to-purple-600")}
              </div>

              {/* Fix Section (Bottom) */}
              <div className="h-40 flex-shrink-0 border-t border-[var(--color-border)] bg-[var(--color-bg-secondary)] flex flex-col">
                   <div className="px-4 py-2 border-b border-[var(--color-border)] flex items-center gap-2 bg-[var(--color-bg-primary)]/50">
                        <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 shadow-sm"></div>
                        <h3 className="font-bold text-xs text-[var(--color-text-secondary)] uppercase tracking-wider">수식 교정 (Fix)</h3>
                        <span className="text-[10px] font-bold text-[var(--color-text-tertiary)] bg-[var(--color-bg-primary)] px-2 py-0.5 rounded-full border border-[var(--color-border)]">
                            {fixJobs.length}
                        </span>
                   </div>
                   <div className="flex-grow overflow-x-auto overflow-y-hidden p-3 flex gap-3 items-center custom-scrollbar">
                        {fixJobs.length === 0 ? (
                            <div className="w-full text-center text-xs text-[var(--color-text-tertiary)] italic opacity-50">
                                수식 교정 대기열이 비어있습니다.
                            </div>
                        ) : (
                            fixJobs.map(job => (
                                <div key={job.id} className="w-64 flex-shrink-0">
                                    <GenerationJobCard 
                                        job={job} 
                                        onDeleteJob={actions.onDeleteJob} 
                                        onCancelJob={actions.onCancelJob} 
                                        onRetryJob={(j) => { setJobToRetry(j); onOpenGenerateModifiedModal(); }} 
                                    />
                                </div>
                            ))
                        )}
                   </div>
              </div>
          </div>
      );
  };

  const renderTrash = () => ( <div className="p-4">{trashedProblems.length === 0 ? <div className="text-center text-zinc-500 py-16"><p>휴지통이 비어있습니다.</p></div> : <><div className="mb-4 text-right"><button onClick={handleEmptyTrashAction} className="text-xs bg-red-800 text-red-200 px-3 py-1 rounded">휴지통 비우기</button></div><div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fill,minmax(500px,1fr))] gap-4">{trashedProblems.map(p => ( <TrashedProblemCard key={p.id} problem={p} onRestore={actions.onRestoreProblem} onPermanentlyDelete={actions.onPermanentlyDeleteProblem} /> ))}</div>{hasNextTrashedPage && <div ref={loadMoreRef} className="col-span-full p-4 flex justify-center">{isFetchingNextTrashed ? <Spinner message="더 많은 항목 로딩 중..." /> : <span className="text-sm text-zinc-600">더 많은 항목을 보려면 스크롤하세요...</span>}</div>}</>}</div> );

  const renderContent = () => {
    switch(type) {
      case 'home': return renderHome();
      case 'folder': return (
        <>
            {Array.from(visitedPaths).map(path => {
                const isActive = path === selection?.id;
                return (
                    <div key={path} style={{ display: isActive ? 'block' : 'none' }}>
                        <FolderViewContent 
                            path={path}
                            isActive={isActive}
                            fileSystemIndex={fileSystemIndex}
                            creatingFolderInPath={creatingFolderInPath}
                            onBreadcrumbClick={handleBreadcrumbClick}
                            onFolderClick={handleFolderClick}
                            setCreatingFolderInPath={setCreatingFolderInPath}
                            onCreateFolder={handleCreateFolder}
                            setContextMenu={setContextMenu}
                            filteredIds={filteredIds}
                            isAiSearching={isAiSearching}
                            hasNextBasePage={hasNextBasePage}
                            isFetchingNextBase={isFetchingNextBase}
                            hasNextGeneratedPage={hasNextGeneratedPage}
                            isFetchingNextGenerated={isFetchingNextGenerated}
                            fetchNextBaseProblems={fetchNextBaseProblems}
                            fetchNextGeneratedProblems={fetchNextGeneratedProblems}
                            loadMoreRef={loadMoreRef}
                            itemRefs={itemRefs}
                            onRegenerateExplanation={actions.onRegenerateExplanation}
                            onCancelExplanationGeneration={actions.onCancelExplanationGeneration}
                            onFixProblemData={onFixProblemData}
                            onToggleLock={onToggleLock}
                            shakingProblemId={shakingProblemId}
                            parentRef={containerRef}
                        />
                    </div>
                );
            })}
        </>
      );
      case 'job': return renderJobs();
      case 'trash': return renderTrash();
      default: return <div>선택된 항목이 없습니다.</div>
    }
  };

  return (
    <div className="flex-grow flex flex-col overflow-hidden relative">
      {/* Header Bar */}
      <div className="flex-shrink-0 bg-[var(--color-bg-secondary)]/95 backdrop-blur-sm border-b border-[var(--color-border-glass)] flex items-center justify-between p-3 gap-3 sticky top-0 z-20">
        <div className="flex-grow flex items-center gap-2 min-w-0">
             <h1 className="text-xl font-bold text-[var(--color-text-primary)] truncate">{title}</h1>
             {selectedIds.size > 0 ? (
                 <div className="flex items-center gap-2 ml-4 bg-[var(--color-accent)]/10 border border-[var(--color-accent)] rounded-md px-3 py-1.5 animate-selection-glow overflow-x-auto">
                     <span className="font-bold text-[var(--color-accent)] whitespace-nowrap mr-2">{selectedIds.size}개 선택됨</span>
                     {/* ... Bulk Action Buttons ... */}
                     <div className="h-4 w-px bg-[var(--color-accent)]/30 mx-1"></div>
                     <div className="flex items-center gap-1">
                        <button onClick={handleMoveSelectionAction} className="text-xs font-bold text-[var(--color-text-primary)] hover:text-[var(--color-accent)] flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5 whitespace-nowrap">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" /><path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" /></svg>이동
                        </button>
                        <button onClick={handleDeleteSelectionAction} className="text-xs font-bold text-rose-400 hover:text-rose-300 flex items-center gap-1 px-2 py-1 rounded hover:bg-rose-500/10 whitespace-nowrap">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>삭제
                        </button>
                     </div>
                     <div className="h-4 w-px bg-[var(--color-accent)]/30 mx-1"></div>
                     {onFixProblemData && (
                         <div className="flex items-center gap-1">
                            <button onClick={() => handleBulkFixMath('problem')} className="text-xs font-bold text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] px-2 py-1 rounded hover:bg-white/5 whitespace-nowrap">✨ 문제교정</button>
                            <button onClick={() => handleBulkFixMath('explanation')} className="text-xs font-bold text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] px-2 py-1 rounded hover:bg-white/5 whitespace-nowrap">✨ 해설교정</button>
                            <button onClick={() => handleBulkRegenerateExplanation('gemini-2.5-flash')} className="text-xs font-bold text-indigo-400 hover:text-indigo-300 px-2 py-1 rounded hover:bg-white/5 whitespace-nowrap">⚡빠른해설</button>
                            <button onClick={() => handleBulkRegenerateExplanation('gemini-2.5-pro')} className="text-xs font-bold text-purple-400 hover:text-purple-300 px-2 py-1 rounded hover:bg-white/5 whitespace-nowrap">🌟다정해설</button>
                         </div>
                     )}
                     <div className="h-4 w-px bg-[var(--color-accent)]/30 mx-1"></div>
                     <button onClick={() => { clearSelectedItems(); setIsSelectionMoving(false); }} className="text-xs text-zinc-400 hover:text-zinc-200 px-2 py-1 whitespace-nowrap">취소</button>
                 </div>
             ) : (
                <div className="relative flex-grow max-w-lg">
                    <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { setIsAiSearching(true); onAiSearch(searchQuery, [...baseProblems, ...problemHistory]).then(ids => { setFilteredIds(new Set(ids)); setIsAiSearching(false); }); }}} placeholder="AI로 문제 검색 (예: '미분계수'가 포함된 쉬운 문제)" className="w-full bg-black/20 border border-[var(--color-border-glass)] rounded-md py-2 px-3 pl-9 text-sm text-white" />
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"><svg className="h-4 w-4 text-zinc-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" /></svg></div>
                </div>
             )}
             {filteredIds && <button onClick={() => { setSearchQuery(''); setFilteredIds(null); }} className="text-xs bg-zinc-700 text-white px-2 py-1 rounded">검색 초기화</button>}
        </div>
        <div className="flex-shrink-0 flex items-center gap-2">
          <button onClick={handleCreateWorksheetAction} className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2 shadow-lg shadow-indigo-500/20 transition-all"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0 1 1 0 002 0z" clipRule="evenodd" /></svg><span className="hidden sm:inline">학습지 만들기</span></button>
          <button onClick={() => setGenerateCreativeModalOpen(true)} className="btn-creative font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg><span className="hidden sm:inline">창작문제</span></button>
          <button onClick={handleCreateModifiedProblemAction} className={`font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2 transition-all border ${selectedIds.size > 0 ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white border-transparent shadow-lg shadow-purple-500/30 transform hover:scale-105' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400 border-zinc-700'}`}><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" /><path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" /></svg><span className="hidden sm:inline">변형문제</span></button>
          <button onClick={() => setAddBaseModalOpen(true)} className="btn-success font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg><span className="hidden sm:inline">새 기반문제</span></button>
        </div>
      </div>
      
      <div ref={containerRef} onMouseDown={handleMouseDown} onScroll={handleScroll} className="flex-grow overflow-y-auto relative">
        {renderContent()}
        {selectionBox && <div className="absolute bg-blue-500/20 border border-blue-400" style={{ left: Math.min(selectionBox.startX, selectionBox.currentX), top: Math.min(selectionBox.startY, selectionBox.currentY), width: Math.abs(selectionBox.currentX - selectionBox.startX), height: Math.abs(selectionBox.currentY - selectionBox.startY), pointerEvents: 'none', zIndex: 100 }}></div>}
      </div>

      {/* ... (Context Menu & Modals remain same) ... */}
      {contextMenu && (
         <div ref={menuRef} className="fixed w-48 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-md shadow-lg z-50 text-sm py-1" style={{ top: contextMenu.y, left: contextMenu.x }}>
            {/* Context Menu Items... */}
            {contextMenu.type === 'folder' && (
                <>
                  <button onClick={() => { handleRenameFolderAction(contextMenu.item.id, contextMenu.item.name); setContextMenu(null); }} className="block w-full text-left px-3 py-1.5 hover:bg-white/10">이름 바꾸기</button>
                  <button onClick={() => { handleDeleteFolderAction(contextMenu.item.id); setContextMenu(null); }} className="block w-full text-left px-3 py-1.5 text-rose-400 hover:bg-rose-500/20">삭제</button>
                </>
            )}
            {contextMenu.type === 'problem' && (
                <>
                  <button onClick={() => { onOpenGenerateModifiedModal(); toggleSelection(contextMenu.item.id); setContextMenu(null); }} className="block w-full text-left px-3 py-1.5 hover:bg-white/10">변형문제 만들기</button>
                  <button onClick={() => handleContentEditAction(contextMenu.item)} className="block w-full text-left px-3 py-1.5 hover:bg-white/10">콘텐츠 편집</button>
                  {onFixProblemData && (
                      <>
                        <button onClick={() => { onFixProblemData(contextMenu.item.id, 'generatedParts' in contextMenu.item, 'problem'); toast.success('문제 수식 교정을 요청했습니다.'); setContextMenu(null); }} className="block w-full text-left px-3 py-1.5 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-white/10">
                            ✨ 문제수식교정
                        </button>
                        <button onClick={() => { onFixProblemData(contextMenu.item.id, 'generatedParts' in contextMenu.item, 'explanation'); toast.success('해설 수식 교정을 요청했습니다.'); setContextMenu(null); }} className="block w-full text-left px-3 py-1.5 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-white/10">
                            ✨ 해설수식교정
                        </button>
                      </>
                  )}
                  <div className="my-1 h-px bg-[var(--color-border)]"></div>
                  <button onClick={() => { onOpenPrintPreview([contextMenu.item]); setContextMenu(null); }} className="block w-full text-left px-3 py-1.5 hover:bg-white/10">인쇄 미리보기</button>
                  <div className="my-1 h-px bg-[var(--color-border)]"></div>
                  <button onClick={() => { handleMoveToTrashAction(contextMenu.item.id, 'generatedParts' in contextMenu.item); setContextMenu(null); }} className="block w-full text-left px-3 py-1.5 text-rose-400 hover:bg-rose-500/20">휴지통으로 이동</button>
                </>
            )}
         </div>
      )}
      
      <AddBaseProblemModal 
        isOpen={isAddBaseModalOpen} 
        onClose={() => setAddBaseModalOpen(false)} 
        onAddMultipleBaseProblems={actions.onAddMultipleBaseProblems} 
        onQueueImport={onQueueImport} 
        allFolderPaths={allFolderPaths}
        onAddFolder={onAddFolder}
      />
      <GenerateCreativeModal isOpen={isGenerateCreativeModalOpen} onClose={() => setGenerateCreativeModalOpen(false)} onRequestGeneration={actions.onRequestGeneration} jobToRetry={null} allFolderPaths={allFolderPaths} />
    </div>
  );
};

export default ContentPane;
